import { useEffect, useState, useCallback, useRef } from "react";
import confetti from "canvas-confetti";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { ShareNetwork, X, Sparkle, CaretRight, Coins } from "@phosphor-icons/react";
import api, { formatBRL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { AchievementIcon } from "../lib/achievementIcons";

export default function AchievementCelebration() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ code: "", link: "" });
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const storyRef = useRef(null);
  const busyRef = useRef(false);

  const fetchNew = useCallback(async () => {
    if (!user || open || busyRef.current) return;
    busyRef.current = true;
    try {
      const { data } = await api.get("/gamification/new-achievements");
      if (data?.achievements?.length) {
        setMeta({ code: data.code, link: data.link });
        setQueue(data.achievements);
        setIdx(0);
        setOpen(true);
      }
    } catch { /* silencioso */ } finally {
      busyRef.current = false;
    }
  }, [user, open]);

  useEffect(() => { fetchNew(); }, [fetchNew]);
  useEffect(() => {
    const onFocus = () => fetchNew();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchNew]);

  useEffect(() => {
    if (open) {
      const cur = queue[idx];
      const color = cur?.color || "#FF6B35";
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.4 }, zIndex: 99999, colors: [color, "#FFD166", "#16A34A", "#004E98"] });
    }
  }, [open, idx, queue]);

  const finish = async () => {
    const ids = queue.map((q) => q.id);
    setOpen(false);
    setQueue([]); setIdx(0);
    try { await api.post("/gamification/achievements/seen", { ids }); } catch { /* noop */ }
  };
  const next = async () => {
    if (idx < queue.length - 1) setIdx(idx + 1);
    else await finish();
  };

  const cur = queue[idx];

  const handleShare = async () => {
    if (!storyRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(storyRef.current, { backgroundColor: null, scale: 1, useCORS: true, logging: false });
      const text = `🏆 Desbloqueei "${cur.label}" no AlugaJá! Alugue ferramentas e equipamentos perto de você. Baixe o app e use meu código ${meta.code}. ${meta.link}`;
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png", 0.95));
      const file = blob ? new File([blob], "alugaja-conquista.png", { type: "image/png" }) : null;
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "AlugaJá", text });
      } else if (navigator.share) {
        await navigator.share({ title: "AlugaJá", text, url: meta.link });
      } else {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = "alugaja-conquista.png";
        a.click();
        try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
        toast.success("Imagem baixada! Compartilhe no seu story 📲");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Não foi possível gerar a imagem.");
    } finally {
      setSharing(false);
    }
  };

  if (!open || !cur) return null;
  const color = cur.color || "#FF6B35";

  return (
    <>
      <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-testid="achievement-celebration">
        <div className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300"
             style={{ background: "linear-gradient(160deg, #0F172A 0%, #1E293B 55%, #004E98 150%)" }}>
          <button onClick={finish} className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80" data-testid="achievement-close-btn">
            <X size={18} weight="bold" />
          </button>
          <div className="px-6 pt-8 pb-6 text-center text-white">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[11px] font-bold mb-4">
              <Sparkle size={12} weight="fill" className="text-[#FFD166]" /> Conquista desbloqueada
            </div>
            <div className="relative mx-auto w-28 h-28 mb-4" data-testid="achievement-medal">
              <div className="absolute inset-0 rounded-full animate-pulse" style={{ background: `radial-gradient(circle, ${color}55, transparent 70%)` }} />
              <div className="absolute inset-2 rounded-full flex items-center justify-center" style={{ background: `radial-gradient(circle at 35% 30%, ${color}, ${color}99)`, border: `3px solid #ffffff55` }}>
                <AchievementIcon icon={cur.icon} size={52} style={{ color: "#fff" }} />
              </div>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-white/60 font-bold">{cur.challenge_title}</div>
            <h2 className="text-2xl font-black mt-1" data-testid="achievement-label">{cur.label}</h2>
            {cur.reward > 0 && (
              <div className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-[#16A34A] text-white text-sm font-black" data-testid="achievement-reward">
                <Coins size={16} weight="fill" /> + {formatBRL(cur.reward)} na carteira
              </div>
            )}
            {queue.length > 1 && (
              <div className="mt-3 text-[11px] text-white/50">{idx + 1} de {queue.length}</div>
            )}
            <div className="mt-6 space-y-2">
              <button onClick={handleShare} disabled={sharing}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#FF6B35] text-white font-bold disabled:opacity-60"
                      data-testid="achievement-share-btn">
                <ShareNetwork size={20} weight="bold" /> {sharing ? "Gerando imagem..." : "Compartilhar conquista"}
              </button>
              <button onClick={next} className="w-full flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl bg-white/10 text-white font-bold"
                      data-testid="achievement-next-btn">
                {idx < queue.length - 1 ? "Próxima" : "Continuar"} <CaretRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Story card (oculto) — 1080x1920 para compartilhar no Instagram/Stories */}
      <div style={{ position: "fixed", left: "-99999px", top: 0, pointerEvents: "none" }} aria-hidden>
        <div ref={storyRef} style={{
          width: 1080, height: 1920, background: "linear-gradient(165deg, #0F172A 0%, #1E293B 50%, #004E98 130%)",
          color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          fontFamily: "Inter, Arial, sans-serif", padding: 80, boxSizing: "border-box", textAlign: "center",
        }}>
          <div style={{ fontSize: 52, fontWeight: 900, letterSpacing: 1 }}>
            <span style={{ color: "#fff" }}>Aluga</span><span style={{ color: "#FF6B35" }}>Já</span>
          </div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "#FFD166", marginTop: 56, textTransform: "uppercase", letterSpacing: 3 }}>
            Conquista desbloqueada
          </div>
          <div style={{ position: "relative", width: 420, height: 420, margin: "60px 0 40px" }}>
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle, ${color}66, transparent 70%)` }} />
            <div style={{ position: "absolute", inset: 50, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                          background: `radial-gradient(circle at 35% 30%, ${color}, ${color}AA)`, border: "10px solid #ffffff44" }}>
              <AchievementIcon icon={cur.icon} size={200} style={{ color: "#fff" }} />
            </div>
          </div>
          <div style={{ fontSize: 34, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{cur.challenge_title}</div>
          <div style={{ fontSize: 96, fontWeight: 900, lineHeight: 1.05, marginTop: 12 }}>{cur.label}</div>
          {cur.reward > 0 && (
            <div style={{ marginTop: 36, fontSize: 48, fontWeight: 900, color: "#16A34A", background: "#ECFDF5", borderRadius: 24, padding: "16px 40px" }}>
              + {formatBRL(cur.reward)}
            </div>
          )}
          <div style={{ marginTop: "auto", width: "100%" }}>
            <div style={{ height: 2, background: "rgba(255,255,255,0.15)", margin: "60px 0 48px" }} />
            <div style={{ fontSize: 46, fontWeight: 900 }}>📲 Baixe o AlugaJá</div>
            <div style={{ fontSize: 34, color: "rgba(255,255,255,0.8)", marginTop: 16 }}>
              Alugue ferramentas e equipamentos perto de você
            </div>
            <div style={{ marginTop: 40, display: "inline-block", background: "#FF6B35", borderRadius: 20, padding: "20px 44px", fontSize: 40, fontWeight: 900 }}>
              Use meu código: {meta.code}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
